<div class="page-header">
  <h1>Releases</h1>
  <button mat-raised-button (click)="syncReleases()" [disabled]="syncing">
    <mat-icon>sync</mat-icon> Sync from IoT Hub
  </button>
</div>

<!-- Releases List -->
<div class="releases-layout">
  <div class="releases-list">
    <mat-card
      *ngFor="let release of releases"
      appearance="outlined"
      class="release-card"
      [class.selected]="release.id === selectedReleaseId"
      (click)="selectRelease(release.id)"
    >
      <div class="release-card-inner">
        <div class="release-card-header">
          <span class="release-version">{{ release.version }}</span>
          <span class="release-date" *ngIf="release.releaseDate">
            {{ release.releaseDate | date: 'dd.MM.yyyy' }}
          </span>
        </div>
        <div *ngIf="release.changelog?.title" class="release-title">{{ release.changelog.title }}</div>
        <div *ngIf="release.changelog" class="release-excerpt">
          {{ getChangelogExcerpt(release.changelog) }}
        </div>
        <div class="release-card-stats">
          <span class="stat" matTooltip="Sites running this release">
            <mat-icon class="inline-icon">check_circle</mat-icon> {{ getCurrentCount(release) }}
          </span>
          <span class="stat" matTooltip="Sites targeting this release">
            <mat-icon class="inline-icon">flag</mat-icon> {{ getDesiredCount(release) }}
          </span>
          <span class="stat" matTooltip="Sites with this release available">
            <mat-icon class="inline-icon">inventory_2</mat-icon> {{ getAvailableCount(release) }}
          </span>
        </div>
      </div>
    </mat-card>
  </div>

  <!-- Detail Panel -->
  <div class="release-detail" *ngIf="selectedRelease">
    <mat-card appearance="outlined" class="detail-card">
      <div class="detail-header">
        <h2>{{ selectedRelease.version }}</h2>
        <div class="detail-date" *ngIf="selectedRelease.releaseDate">
          Released {{ selectedRelease.releaseDate | date: 'dd.MM.yyyy HH:mm':'UTC' }} UTC
        </div>
      </div>

      <!-- Stats summary -->
      <div class="stats-row">
        <div class="stat-box current-stat">
          <span class="stat-number">{{ getCurrentCount(selectedRelease) }}</span>
          <span class="stat-label">Running</span>
        </div>
        <div class="stat-box desired-stat">
          <span class="stat-number">{{ getDesiredCount(selectedRelease) }}</span>
          <span class="stat-label">Desired</span>
        </div>
        <div class="stat-box available-stat">
          <span class="stat-number">{{ getAvailableCount(selectedRelease) }}</span>
          <span class="stat-label">Available</span>
        </div>
      </div>

      <!-- Changelog (on top) -->
      <mat-expansion-panel [expanded]="true" class="detail-panel">
        <mat-expansion-panel-header>
          <mat-panel-title>Changelog</mat-panel-title>
        </mat-expansion-panel-header>

        <div *ngIf="!editingChangelog">
          <div *ngIf="selectedRelease.changelog as cl" class="changelog-view">
            <div class="changelog-meta">
              <div class="meta-row">
                <span class="meta-label">Title</span>
                <span class="meta-value">{{ cl.title }}</span>
              </div>
              <div class="meta-row" *ngIf="cl.heading">
                <span class="meta-label">Heading</span>
                <span class="meta-value">{{ cl.heading }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">Version label</span>
                <span class="meta-value">{{ cl.releaseVersion }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">Environments</span>
                <span class="meta-value">
                  <span *ngFor="let env of cl.environment" class="env-chip">{{ getEnvironmentLabel(env) }}</span>
                  <span *ngIf="!cl.environment?.length" class="empty-state">None</span>
                </span>
              </div>
              <div class="meta-row">
                <span class="meta-label">Manual link</span>
                <span class="meta-value">
                  <a *ngIf="cl.linkToManual" [href]="cl.linkToManual" target="_blank" rel="noopener noreferrer">{{
                    cl.linkToManual
                  }}</a>
                  <span *ngIf="!cl.linkToManual" class="empty-state">Not set</span>
                </span>
              </div>
              <div class="meta-row" *ngIf="getChangelogImageUrl(cl) as imgUrl">
                <span class="meta-label">Image</span>
                <span class="meta-value"><img [src]="imgUrl" alt="Changelog image" class="changelog-image" /></span>
              </div>
            </div>

            <div class="rendered-page" [innerHTML]="getSafeHtml(cl.htmlContent)"></div>
          </div>
          <p *ngIf="!selectedRelease.changelog" class="empty-state">No changelog set for this release.</p>
          <div class="view-actions">
            <button mat-raised-button color="primary" (click)="startEditChangelog()">
              <mat-icon>edit</mat-icon>
              {{ selectedRelease.changelog ? 'Edit Changelog' : 'Add Changelog' }}
            </button>
            <button
              mat-raised-button
              color="warn"
              *ngIf="selectedRelease.changelog"
              (click)="removeChangelog()"
              [disabled]="savingChangelog"
            >
              <mat-icon>delete</mat-icon> Remove Changelog
            </button>
          </div>
        </div>

        <div *ngIf="editingChangelog" class="changelog-editor">
          <!-- Structured fields -->
          <div class="editor-fields">
            <mat-form-field class="field-half">
              <mat-label>Title</mat-label>
              <input matInput [(ngModel)]="clTitle" required placeholder="e.g. Generic Dashboard now available" />
            </mat-form-field>
            <mat-form-field class="field-half">
              <mat-label>Heading (optional)</mat-label>
              <input matInput [(ngModel)]="clHeading" placeholder="Shorter variant of the title" />
            </mat-form-field>
            <mat-form-field class="field-third">
              <mat-label>Release version label</mat-label>
              <input matInput [(ngModel)]="clReleaseVersion" placeholder="e.g. 2.3.0" />
            </mat-form-field>
            <mat-form-field class="field-third">
              <mat-label>Environments</mat-label>
              <mat-select [(ngModel)]="clEnvironments" multiple>
                <mat-option *ngFor="let opt of environmentOptions" [value]="opt.value">{{ opt.label }}</mat-option>
              </mat-select>
            </mat-form-field>
            <mat-form-field class="field-third">
              <mat-label>Link to manual</mat-label>
              <input matInput [(ngModel)]="clLinkToManual" placeholder="https://www.scanreach.com/support" />
            </mat-form-field>
          </div>

          <!-- Main image -->
          <div class="main-image-row">
            <span class="main-image-label">Image (shown above the content):</span>
            <input
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              (change)="onMainImageSelect($event)"
              #mainImageInput
              style="display: none"
            />
            <button mat-stroked-button (click)="mainImageInput.click()">
              <mat-icon>image</mat-icon> {{ clImage ? 'Replace Image' : 'Upload Image' }}
            </button>
            <button mat-stroked-button color="warn" *ngIf="clImage" (click)="removeMainImage()">
              <mat-icon>close</mat-icon> Remove
            </button>
          </div>
          <div *ngIf="clImageDataUrl" class="main-image-preview">
            <img [src]="clImageDataUrl" alt="Changelog image preview" />
          </div>

          <div class="editor-mode-toggle">
            <button mat-button [class.active]="editorMode === 'source'" (click)="editorMode = 'source'">
              <mat-icon>code</mat-icon> HTML Source
            </button>
            <button mat-button [class.active]="editorMode === 'preview'" (click)="editorMode = 'preview'">
              <mat-icon>visibility</mat-icon> Preview
            </button>
          </div>

          <div *ngIf="editorMode === 'source'" class="editor-source">
            <p class="editor-hint">Enter HTML content. Paste images directly or use the file picker.</p>
            <textarea
              [(ngModel)]="clHtmlContent"
              (paste)="onImagePaste($event)"
              rows="18"
              class="changelog-textarea"
              placeholder='<div style="display: flex; flex-flow: column; gap: 16px">&#10;  <p>We are excited to announce…</p>&#10;  <h4>Highlights:</h4>&#10;  <ul>&#10;    <li>…</li>&#10;  </ul>&#10;</div>'
            ></textarea>
          </div>

          <div *ngIf="editorMode === 'preview'" class="editor-rendered-preview">
            <p class="editor-hint">
              Approximation of the “What's new” dialog in the Connect web app (Vue) where this changelog is shown.
            </p>
            <div class="vue-dialog-backdrop">
              <div class="vue-dialog">
                <div class="vue-dialog-header">
                  <div class="vue-dialog-titles">
                    <span class="vue-dialog-heading" *ngIf="clHeading">{{ clHeading }}</span>
                    <span class="vue-dialog-title">{{ clTitle || 'Title goes here' }}</span>
                  </div>
                  <span class="vue-dialog-version" *ngIf="clReleaseVersion">v{{ clReleaseVersion }}</span>
                </div>
                <div class="vue-dialog-body">
                  <img *ngIf="clImageDataUrl" [src]="clImageDataUrl" alt="Release image" class="vue-dialog-image" />
                  <div *ngIf="clHtmlContent" [innerHTML]="getSafeHtml(clHtmlContent)"></div>
                  <p *ngIf="!clHtmlContent" class="empty-state">
                    Nothing to preview yet. Switch to HTML Source and add content.
                  </p>
                </div>
                <div class="vue-dialog-footer">
                  <span class="vue-env-chips">
                    <span *ngFor="let env of clEnvironments" class="env-chip">{{ getEnvironmentLabel(env) }}</span>
                  </span>
                  <a
                    *ngIf="clLinkToManual"
                    [href]="clLinkToManual"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="vue-dialog-button secondary"
                    >I would like more info</a
                  >
                  <span class="vue-dialog-button primary">Close</span>
                </div>
              </div>
            </div>
          </div>

          <div class="editor-actions">
            <input
              type="file"
              accept="image/*"
              (change)="onImageFileSelect($event)"
              #imageInput
              style="display: none"
            />
            <span class="spacer"></span>
            <button mat-raised-button (click)="cancelEditChangelog()">Cancel</button>
            <button mat-raised-button color="primary" (click)="saveChangelog()" [disabled]="savingChangelog">
              Save Changelog
            </button>
          </div>
        </div>
      </mat-expansion-panel>

      <!-- Containers -->
      <mat-expansion-panel class="detail-panel">
        <mat-expansion-panel-header>
          <mat-panel-title
            >Containers <span class="count-badge">{{ selectedRelease.containers.length }}</span></mat-panel-title
          >
        </mat-expansion-panel-header>
        <div class="containers-grid">
          <div *ngFor="let c of selectedRelease.containers" class="container-item">
            <span class="container-name">{{ c.containerName }}</span>
            <span class="container-version" [class.container-changed]="isContainerChanged(selectedRelease, c)">{{
              c.containerVersion
            }}</span>
          </div>
        </div>
      </mat-expansion-panel>

      <!-- Sites Running (Current) -->
      <mat-expansion-panel class="detail-panel">
        <mat-expansion-panel-header>
          <mat-panel-title
            >Sites Running
            <span class="count-badge current-count">{{ getCurrentCount(selectedRelease) }}</span></mat-panel-title
          >
        </mat-expansion-panel-header>
        <div *ngIf="getCurrentCount(selectedRelease) === 0" class="empty-state">No sites reporting this release.</div>
        <div class="sites-grid">
          <div *ngFor="let item of selectedRelease.assignedSites">
            <div *ngIf="item.isCurrent" class="site-item">
              <span class="site-name">{{ item.site.name }}</span>
              <span class="site-badge current">Running</span>
            </div>
          </div>
        </div>
      </mat-expansion-panel>

      <!-- Sites Desired -->
      <mat-expansion-panel class="detail-panel">
        <mat-expansion-panel-header>
          <mat-panel-title
            >Sites Targeting
            <span class="count-badge desired-count">{{ getDesiredCount(selectedRelease) }}</span></mat-panel-title
          >
        </mat-expansion-panel-header>
        <div *ngIf="getDesiredCount(selectedRelease) === 0" class="empty-state">No sites targeting this release.</div>
        <div class="sites-grid">
          <div *ngFor="let item of selectedRelease.assignedSites">
            <div *ngIf="item.isDesired" class="site-item">
              <span class="site-name">{{ item.site.name }}</span>
              <span class="site-badge desired">Desired</span>
            </div>
          </div>
        </div>
      </mat-expansion-panel>

      <!-- Sites Available -->
      <mat-expansion-panel class="detail-panel">
        <mat-expansion-panel-header>
          <mat-panel-title
            >Sites with Access
            <span class="count-badge available-count">{{ getAvailableCount(selectedRelease) }}</span></mat-panel-title
          >
        </mat-expansion-panel-header>
        <div *ngIf="getAvailableCount(selectedRelease) === 0" class="empty-state">
          No sites have this release available.
        </div>
        <div class="sites-grid">
          <div *ngFor="let item of selectedRelease.assignedSites" class="site-item">
            <span class="site-name">{{ item.site.name }}</span>
            <span *ngIf="item.isCurrent" class="site-badge current">Running</span>
            <span *ngIf="item.isDesired && !item.isCurrent" class="site-badge desired">Desired</span>
            <span *ngIf="!item.isCurrent && !item.isDesired" class="site-badge available">Available</span>
          </div>
        </div>
      </mat-expansion-panel>
    </mat-card>
  </div>

  <!-- Empty state when nothing selected -->
  <div class="release-detail empty-detail" *ngIf="!selectedRelease">
    <div class="select-prompt">
      <mat-icon class="prompt-icon">touch_app</mat-icon>
      <p>Select a release from the list to view details</p>
    </div>
  </div>
</div>
